import { clamp } from "lodash-es";

import {
  CLICK_THRESHOLD,
  DOUBLE_CLICK_DELAY,
  HORIZONTAL_SCROLLBAR_HEIGHT,
  LONG_PRESS_TIMEOUT,
  REGION_DRAG_INDICATOR_WIDTH,
  VERTICAL_SCROLLBAR_WIDTH,
} from "../../settings.ts";
import { type GenomeViewerStore, useGenomeViewerStore } from "../../store/store.ts";
import type { WorkerMessageHoverBase } from "../../track-workers/types.ts";
import { getTrackGroupIndexByY } from "../../utils/get-trackgroup-index-by-y.ts";
import { getHoveredTrack } from "../../utils/graphics/get-hovered-track.ts";
import {
  getCanvasCoordinates,
  getHorizontalScrollbarWidth,
  getHorizontalScrollbarX,
  getVerticalScrollbarHeight,
  getVerticalScrollbarY,
  isHorizontalScrollbarClicked,
  isVerticalScrollbarClicked,
} from "../../utils/graphics/scrollbar-graphics.ts";
import { getTrackOffsetY } from "../../utils/graphics/track-offset.ts";
import { refreshTracks } from "../messages/post-track-messages.ts";
import { refreshMain } from "../render-main.ts";
import { getAllTracksHeight } from "../utils.ts";
import { handleClick, handleDoubleClick, handleScroll, handleZoom } from "./user-events-utils.ts";

let pointerStartPos: { x: number; y: number } | undefined = undefined;

// Variables to track double clicks
let lastClickTime = 0;
let longPressTimeoutRef = 0;

/**
 * Mouse wheel: Scroll down or zoom
 */
export function handleWheel(e: WheelEvent) {
  e.preventDefault();
  if (useGenomeViewerStore.getState().zoomRange) {
    return;
  }

  // Closes the contextual menu when zooming/scrolling
  const contextualMenu: HTMLDivElement = document.querySelector("#genome-viewer-contextual-menu")!;
  Object.assign(contextualMenu.style, {
    display: "none",
  });
  useGenomeViewerStore.setState({ showFeaturePopover: false, contextualMenuData: undefined });

  if (e.ctrlKey) {
    const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9;
    handleZoom(zoomFactor, e.offsetX);
  } else {
    handleScroll(e.deltaX, e.deltaY, true);
    updateHoveredTrack(e);
  }
}

export function handlePointerDown(e: PointerEvent) {
  if (!("button" in e)) return;

  const state = useGenomeViewerStore.getState();
  const { x, y } = getCanvasCoordinates(e, state.canvas!.getBoundingClientRect());
  pointerStartPos = { x, y };

  const storeUpdate: Partial<GenomeViewerStore> = {};

  // Right click
  if (e.button === 2) {
    const contextualMenu: HTMLDivElement = document.querySelector("#genome-viewer-contextual-menu")!;
    storeUpdate.showFeaturePopover = false;
    storeUpdate.zoomRange = undefined;
    storeUpdate.contextualMenuData = state.hoverData?.id ? { ...state.hoverData } : undefined;

    Object.assign(contextualMenu.style, {
      display: "none",
    });

    // Left click
  } else if (e.button === 0) {
    // Set selection start
    if (e.ctrlKey) {
      storeUpdate.zoomRange = { startX: x, stopX: x };
      storeUpdate.selectedRange = undefined;
    } else if (e.shiftKey) {
      const startBase = Math.floor((x + state.scrollX) * state.basePerPixel);
      const startY = y + state.scrollY;

      const startTrackGroupIndex = getTrackGroupIndexByY(y);
      const trackGroupIds = [...state.trackGroups.keys()];

      const trackGroupsInSelection = startTrackGroupIndex
        ? trackGroupIds.slice(startTrackGroupIndex, startTrackGroupIndex + 1)
        : trackGroupIds;

      storeUpdate.selectedRange = { start: startBase, stop: startBase, startY: startY, stopY: startY };
      storeUpdate.trackGroupsInSelection = state.fullRangeSelection ? trackGroupIds : trackGroupsInSelection;
    } else {
      longPressTimeoutRef = window.setTimeout(() => {
        useGenomeViewerStore.setState({ asynchronousScrolling: true });
        refreshMain();
      }, LONG_PRESS_TIMEOUT);
    }

    storeUpdate.leftSelectionDragging = false;
    storeUpdate.rightSelectionDragging = false;

    if (state.selectedRange) {
      if (state.leftSelectionHovering) {
        storeUpdate.leftSelectionDragging = true;
      }

      if (state.rightSelectionHovering) {
        storeUpdate.rightSelectionDragging = true;
      }
    }

    if (isVerticalScrollbarClicked(x, y, state.canvas!.width)) {
      storeUpdate.isVerticalScrolling = true;
      storeUpdate.scrollbarClickOffset = y - getVerticalScrollbarY(state.canvas!.height, state.scrollY);
    } else if (isHorizontalScrollbarClicked(x, y, state.canvas!.height)) {
      const maxWidth = useGenomeViewerStore.getState().getMaxWidth()[1];
      storeUpdate.isHorizontalScrolling = true;
      storeUpdate.scrollbarClickOffset = x - getHorizontalScrollbarX(state.scrollX, maxWidth, state.canvas!.width);
    }

    if (!(state.isVerticalScrolling || state.isHorizontalScrolling)) {
      storeUpdate.isDragging = true;
      storeUpdate.lastX = e.clientX;
      storeUpdate.lastY = e.clientY;
      state.canvas!.setPointerCapture(e.pointerId);
    }
  }

  useGenomeViewerStore.setState(storeUpdate);
}

export function handlePointerMove(e: PointerEvent | WheelEvent) {
  const state = useGenomeViewerStore.getState();

  window.clearTimeout(longPressTimeoutRef);

  if (!state.canvas) {
    return;
  }

  const storeUpdate: Partial<GenomeViewerStore> = {};

  const boundingRect = state.canvas!.getBoundingClientRect();
  const { x, y } = getCanvasCoordinates(e, boundingRect);

  storeUpdate.leftSelectionHovering = false;
  storeUpdate.rightSelectionHovering = false;

  if (state.selectedRange) {
    const startRangeX = state.selectedRange.start / state.basePerPixel - state.scrollX;
    const stopRangeX = state.selectedRange.stop / state.basePerPixel - state.scrollX;

    if (startRangeX - REGION_DRAG_INDICATOR_WIDTH <= x && x <= startRangeX + REGION_DRAG_INDICATOR_WIDTH) {
      storeUpdate.leftSelectionHovering = true;
    }

    if (stopRangeX - REGION_DRAG_INDICATOR_WIDTH <= x && x <= stopRangeX + REGION_DRAG_INDICATOR_WIDTH) {
      storeUpdate.rightSelectionHovering = true;
    }
  }

  if (state.isDragging) {
    const deltaX = e.clientX - state.lastX;
    const deltaY = e.clientY - state.lastY;

    storeUpdate.lastX = e.clientX;
    storeUpdate.lastY = e.clientY;
    storeUpdate.hoverData = undefined; // just remove it while dragging, because it's outdated now. Has to move the mouse to trigger it again though.
    if (state.selectedRange) {
      if (state.leftSelectionDragging) {
        const start = Math.ceil(
          ((x < state.canvas.width ? (x > 0 ? x : 0) : state.canvas.width) + state.scrollX) * state.basePerPixel,
        );

        storeUpdate.selectedRange = { ...state.selectedRange, start };
        useGenomeViewerStore.setState(storeUpdate);
        refreshMain();
        return;
      }

      if (state.rightSelectionDragging) {
        const stop = Math.ceil(
          ((x < state.canvas.width ? (x > 0 ? x : 0) : state.canvas.width) + state.scrollX) * state.basePerPixel,
        );

        storeUpdate.selectedRange = { ...state.selectedRange, stop };
        useGenomeViewerStore.setState(storeUpdate);
        refreshMain();
        return;
      }
    }

    // Zoom selection
    if (e.ctrlKey && state.zoomRange?.startX) {
      storeUpdate.zoomRange = { ...state.zoomRange, stopX: clamp(x, 0, state.canvas.width) };
      useGenomeViewerStore.setState(storeUpdate);
      refreshMain();
      return;
    }

    // Region selection
    if (e.shiftKey && state.selectedRange?.start !== undefined) {
      const stop = Math.ceil(
        ((x < state.canvas.width ? (x > 0 ? x : 0) : state.canvas.width) + state.scrollX) * state.basePerPixel,
      );

      const stopY = y + state.scrollY;
      const trackGroupIds = [...state.trackGroups.keys()];

      const startTrackGroupIndex = getTrackGroupIndexByY(state.selectedRange.startY) ?? trackGroupIds.length - 1;
      const stopTrackGroupIndex = getTrackGroupIndexByY(stopY) ?? (y > 0 ? trackGroupIds.length - 1 : 0);

      const startIndex = Math.min(startTrackGroupIndex, stopTrackGroupIndex);
      const endIndex = Math.max(startTrackGroupIndex, stopTrackGroupIndex) + 1;
      const trackGroupsInSelection = trackGroupIds.slice(startIndex, endIndex);

      storeUpdate.selectedRange = { ...state.selectedRange, stop };
      storeUpdate.trackGroupsInSelection = state.fullRangeSelection ? trackGroupIds : trackGroupsInSelection;
      useGenomeViewerStore.setState(storeUpdate);
      refreshMain();
      return;
    }

    if (!(state.isVerticalScrolling || state.isHorizontalScrolling)) {
      useGenomeViewerStore.setState(storeUpdate);
      handleScroll(-deltaX, -deltaY);
    }
  } else {
    updateHoveredTrack(e);

    useGenomeViewerStore.setState(storeUpdate);
    refreshMain();
    // We need to call refreshMain() to update the hover effect of the bars for the selection of a region
  }
}

export function handlePointerUp(e: PointerEvent) {
  const state = useGenomeViewerStore.getState();
  const currentTime = new Date().getTime();
  const { x, y } = getCanvasCoordinates(e, state.canvas!.getBoundingClientRect());
  const storeUpdate: Partial<GenomeViewerStore> = {
    isDragging: false,
    isDraggingRegionIndicator: false,
    leftSelectionDragging: false,
    rightSelectionDragging: false,
    asynchronousScrolling: false,
  };

  window.clearTimeout(longPressTimeoutRef);

  // Zoom into selection
  if (state.zoomRange) {
    state.zoomToRange();
  }

  // Reset zoom selection
  state.zoomRange = undefined;

  if (state.selectedRange) {
    storeUpdate.selectedRange = {
      ...state.selectedRange,
      stopY: y + state.scrollY,
    };
  }

  // Align the bases on the grid with the base shift of the track
  for (const trackGroup of state.trackGroups.values()) {
    // Baseshift is stored as a float when async scrolling to prevent snapping on the grid when moving a track
    trackGroup.graphics.baseShift = Math.round(trackGroup.graphics.baseShift);
  }

  if (pointerStartPos) {
    const dx = Math.abs(x - pointerStartPos.x);
    const dy = Math.abs(y - pointerStartPos.y);

    // If the mouse pointer didn't move much, we consider it to be a click
    // TODO we should still verify if we didn't click on the scroll bar or any elements above the tracks shapes.
    if (dx < CLICK_THRESHOLD && dy < CLICK_THRESHOLD) {
      // Check if this is a double click (two clicks within DOUBLE_CLICK_DELAY ms)
      if (currentTime - lastClickTime < DOUBLE_CLICK_DELAY && "button" in e && e.button === 0) {
        // This is a double click
        handleDoubleClick(e);
        // Reset the timer to prevent triple click being detected as a new double click
        lastClickTime = 0;
      } else {
        // This is potentially the first click of a double-click
        // So we store the timestamp but delay the single-click action
        lastClickTime = currentTime;

        handleClick(e, storeUpdate);
      }
    } else {
      storeUpdate.contextualMenuData = undefined;
    }
  }

  useGenomeViewerStore.setState(storeUpdate);

  pointerStartPos = undefined;

  state.canvas!.releasePointerCapture(e.pointerId);

  // refresh selection, ...
  refreshTracks();
  refreshMain();
}

export function handlePointerCancel(_e: PointerEvent) {
  useGenomeViewerStore.setState({ isDragging: false, asynchronousScrolling: false });
}

// Outside of the canvas
export function handleScrollbarMouseMove(e: PointerEvent) {
  const state = useGenomeViewerStore.getState();
  const { x, y } = getCanvasCoordinates(e, state.canvas!.getBoundingClientRect());
  const storeUpdate: Partial<GenomeViewerStore> = {};
  const [maxTrackGroupWidth, maxWidth] = useGenomeViewerStore.getState().getMaxWidth();

  if (state.canvas && (x > state.canvas.width || x < 0 || y > state.canvas.height || y < 0)) {
    storeUpdate.hoveredGroupId = undefined;
  }

  let newHoveringHorizontalScrollBar: boolean;
  let newHoveringVerticalScrollBar: boolean;

  // If the horizontal scrollbar is hovered
  if (y > state.canvas!.height - HORIZONTAL_SCROLLBAR_HEIGHT - 2 && y < state.canvas!.height) {
    newHoveringHorizontalScrollBar = true;
  } else {
    newHoveringHorizontalScrollBar = false;
  }

  // If the vertical scrollbar is hovered
  if (x > state.canvas!.width - VERTICAL_SCROLLBAR_WIDTH - 2 && x < state.canvas!.width) {
    newHoveringVerticalScrollBar = true;
  } else {
    newHoveringVerticalScrollBar = false;
  }

  if (
    (newHoveringHorizontalScrollBar !== state.hoveringHorizontalScrollbar ||
      newHoveringVerticalScrollBar !== state.hoveringVerticalScrollbar) &&
    !state.isDragging
  ) {
    storeUpdate.hoveringHorizontalScrollbar = newHoveringHorizontalScrollBar;
    storeUpdate.hoveringVerticalScrollbar = newHoveringVerticalScrollBar;
    refreshMain();
  }

  if (state.isVerticalScrolling) {
    const allTracksHeight = getAllTracksHeight();
    const scrollableHeight = allTracksHeight - state.canvas!.height;
    const scrollbarY = y - state.scrollbarClickOffset;
    const scrollRatio = scrollbarY / (state.canvas!.height - getVerticalScrollbarHeight(state.canvas!.height));

    storeUpdate.scrollY = Math.floor(Math.max(0, Math.min(scrollRatio * scrollableHeight, scrollableHeight)));
  } else if (state.isHorizontalScrolling && maxWidth > (state.canvas?.width ?? 0)) {
    const scrollableWidth = maxWidth - state.canvas!.width;
    const scrollbarX = x - state.scrollbarClickOffset;
    const scrollRatio = scrollbarX / (state.canvas!.width - getHorizontalScrollbarWidth(maxWidth, state.canvas!.width));
    storeUpdate.scrollX = clamp(
      Math.floor(scrollRatio * scrollableWidth),
      state.getOriginOffset(),
      Math.max(maxTrackGroupWidth - state.ctx!.canvas.width, state.scrollX),
    );
  }

  useGenomeViewerStore.setState(storeUpdate);

  // No need to call refreshMain() when we change hoveredGroupId because we don't render hover effect on the canvas
  if (storeUpdate.scrollX != null || storeUpdate.scrollY != null) {
    refreshTracks();
  }
}

export function handleScrollbarMouseUp() {
  useGenomeViewerStore.setState({
    isVerticalScrolling: false,
    isHorizontalScrolling: false,
    isDraggingRegionIndicator: false,
  });
}

function updateHoveredTrack(e: PointerEvent | WheelEvent) {
  const state = useGenomeViewerStore.getState();
  const { x, y } = getCanvasCoordinates(e, state.canvas!.getBoundingClientRect());
  const hoveredTrack = getHoveredTrack(state.trackGroups, state.tracks, y + state.scrollY);
  if (hoveredTrack) {
    const { groupId: hoveredGroupId, trackId: hoveredTackId } = hoveredTrack;
    // Handle hover state, e.g., change cursor or highlight track
    // You might want to store the hovered track in the state
    if (hoveredGroupId !== state.hoveredGroupId) {
      useGenomeViewerStore.setState({ hoveredGroupId });
    }

    if (hoveredTackId !== state.hoveredTackId) {
      useGenomeViewerStore.setState({ hoveredTackId });
    }

    const localY = y - getTrackOffsetY(state.trackGroups, state.tracks, hoveredTackId) + state.scrollY;

    hoveredTrack.worker.postMessage({
      type: "hover",
      x, // <- localX
      y: localY,
      basePerPixel: state.basePerPixel,
      scrollShiftX: state.scrollX % (1 / state.basePerPixel),
    } satisfies WorkerMessageHoverBase);
  } else if (state.hoveredGroupId !== undefined) {
    useGenomeViewerStore.setState({ hoveredGroupId: undefined });
  }
}
